/**
 * The statistical alarm (plant-analytics S15): an alarm rule whose condition is
 * `{"op": "spc", "chart_id": "<uuid>", "rules": ["we1", "we2"]}` fires on a control chart's new live
 * signals, never on its history. Backend `internal/iot/alarms_spc.go` (the grammar and its 422s) and
 * `alarms_spc_eval.go` (the evaluation, every 2 minutes in the background).
 *
 * The rule form picks one of the target tag's active charts (`GET /v1/twin/spc/charts?tag_id=`) and ticks
 * the rules; these helpers hold the words and build the payload. The server refuses any key but op,
 * chart_id and rules, so the payload carries nothing else.
 */
import { spcMethodLabel, type SpcChart, type SpcChartList } from "@/lib/api/spc";

export type SpcAlarmRuleName = "we1" | "we2" | "we3" | "we4" | "ewma" | "cusum_upper" | "cusum_lower";

/** The seven rules a statistical alarm can watch, in the form's order, in plain words. */
export const SPC_ALARM_RULES: ReadonlyArray<{ rule: SpcAlarmRuleName; label: string }> = [
  { rule: "we1", label: "One point beyond 3 sigma (we1)" },
  { rule: "we2", label: "2 of 3 points beyond 2 sigma (we2)" },
  { rule: "we3", label: "4 of 5 points beyond 1 sigma (we3)" },
  { rule: "we4", label: "8 points in a row on one side (we4)" },
  { rule: "ewma", label: "Smoothed average beyond its limits (ewma)" },
  { rule: "cusum_upper", label: "Sustained upward shift (cusum_upper)" },
  { rule: "cusum_lower", label: "Sustained downward shift (cusum_lower)" },
];

/** The line the form shows under the rules. */
export const SPC_ALARM_LIVE_ONLY = "Fires only on new live data from the chart, never on its history.";

const RULE_NAMES = SPC_ALARM_RULES.map((r) => r.rule);

/** A rule's plain label; an unknown name reads as itself. */
export function spcAlarmRuleLabel(rule: string): string {
  return SPC_ALARM_RULES.find((r) => r.rule === rule)?.label ?? rule;
}

/**
 * The rules a chart type raises: the run rules on individual values and hourly averages, the EWMA rule on
 * an EWMA chart, the two CUSUM rules on a CUSUM chart. An unknown type offers all seven.
 */
export function spcAlarmRulesFor(chartType: string | undefined): SpcAlarmRuleName[] {
  switch (chartType) {
    case "imr":
    case "xbar_r":
    case "xbar_s":
      return ["we1", "we2", "we3", "we4"];
    case "ewma":
      return ["ewma"];
    case "cusum":
      return ["cusum_upper", "cusum_lower"];
    default:
      return [...RULE_NAMES];
  }
}

/** The charts a statistical alarm on a tag can watch: the tag's active tag charts, in the server's order. */
export function spcAlarmCharts(charts: readonly SpcChart[] | undefined, tagId: string): SpcChart[] {
  return (charts ?? []).filter((c) => c.status === "active" && c.subject_kind === "tag" && c.tag_id === tagId);
}

/**
 * What the form says when the target tag has no chart of its own but the list holds the chart of its port: a port fed by
 * several sources is charted once, as one series, on the tag that has fed it longest, and the list for any of its
 * sources holds that chart. A statistical alarm watches a chart of its own target tag, so the form points to that tag,
 * where saying the tag has not been charted would be wrong. null when the list holds no such chart.
 */
export function spcAlarmPortChartNote(list: SpcChartList | undefined, tagId: string): string | null {
  const port = (list?.charts ?? []).some(
    (c) => c.status === "active" && c.subject_kind === "tag" && c.tag_id !== null && c.tag_id !== tagId,
  );
  if (!port) return null;
  return (
    "This tag feeds a port that is charted as one series, on the tag that has fed the port longest. " +
    "A statistical alarm watches a chart of its own target tag, so set this alarm on that tag to watch the port."
  );
}

/** A chart as the form's picker reads it: its kind in words and type, a companion or provisional limits noted. */
export function spcAlarmChartLabel(chart: SpcChart): string {
  const kind = spcMethodLabel(chart.limits?.method ?? chart.chart_type).label;
  const notes: string[] = [chart.chart_type];
  if (chart.params?.companion_of) notes.push("companion");
  if (chart.limits?.provisional) notes.push("provisional limits");
  return `${kind} (${notes.join(", ")})`;
}

/** The condition the server stores for a statistical alarm. */
export interface SpcAlarmConditionBody {
  op: "spc";
  chart_id: string;
  rules?: SpcAlarmRuleName[];
}

/**
 * The condition a rule form sends: the chart and the ticked rules, in the list's order, each once. No rule
 * ticked sends no `rules` (the server reads that as every rule); the form asks for at least one.
 */
export function buildSpcAlarmCondition(chartId: string, rules: readonly string[]): SpcAlarmConditionBody {
  const ticked = RULE_NAMES.filter((r) => rules.includes(r));
  return ticked.length > 0 ? { op: "spc", chart_id: chartId, rules: ticked } : { op: "spc", chart_id: chartId };
}

/**
 * Ticks or unticks a rule in the form. The result keeps only the rules the chart offers, so a rule the chart
 * never raises (left ticked by a stored "any rule") is not saved once the user changes the ticks.
 */
export function spcToggleAlarmRule(
  rules: readonly string[],
  offered: readonly string[],
  rule: string,
  on: boolean,
): string[] {
  const kept = rules.filter((r) => offered.includes(r) && r !== rule);
  return on ? [...kept, rule] : kept;
}

/**
 * The ticked rules the chart offers, in their order (#926 L3): the form validates and saves only these, so a rule
 * the chart's type never raises (the server refuses it, 422 spc_rule_not_raised) is never sent.
 */
export function spcAlarmKeepOffered(rules: readonly string[], offered: readonly string[]): string[] {
  return rules.filter((r) => offered.includes(r));
}

/** Whether a form's statistical alarm can be saved: a chart and at least one rule. */
export function spcAlarmDraftValid(chartId: string, rules: readonly string[]): boolean {
  return chartId.trim() !== "" && rules.some((r) => (RULE_NAMES as readonly string[]).includes(r));
}

/** A statistical alarm's condition in words, for the rule card and the event detail. */
export function spcAlarmConditionText(rules: readonly string[] | undefined): string {
  if (!rules || rules.length === 0) return "Control chart signal, any rule";
  return `Control chart signal: ${rules.join(", ")}`;
}

/** The control chart signal a statistical alarm event fired on (its `message.spc`). */
export interface SpcAlarmEventInfo {
  chartId: string;
  rule?: string;
  /** The signal's point (ISO). */
  at?: string;
  /** The sub-chart it broke on: i, mr, xbar, r, s, ewma, cusum. */
  subChart?: string;
  /** How many live signals the event covers. */
  signals?: number;
}

/** Reads `message.spc` of an alarm event; undefined for any other alarm. */
export function spcAlarmEventInfo(message: unknown): SpcAlarmEventInfo | undefined {
  if (!message || typeof message !== "object") return undefined;
  const spc = (message as Record<string, unknown>).spc;
  if (!spc || typeof spc !== "object") return undefined;
  const o = spc as Record<string, unknown>;
  if (typeof o.chart_id !== "string" || o.chart_id === "") return undefined;
  const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : undefined);
  return {
    chartId: o.chart_id,
    rule: str(o.rule),
    at: str(o.at),
    subChart: str(o.sub_chart),
    signals: typeof o.signals === "number" ? o.signals : undefined,
  };
}
